/**
 * The eight error classes a correction carries.
 *
 * The set is owned by the backend (`services/correction_classes.py`) and served by
 * `GET /correction/classes`; these literals mirror it so TypeScript can discriminate on
 * them. The FRENCH labels live in `utils/correction-classes.ts` — a class name is an
 * identifier that travels to the database, a label is text that gets translated, and
 * keeping them apart is what lets the wording change without rewriting stored rows.
 *
 * Each class earns its place by pointing at a repair no other one points at: document
 * selection, prompt/few-shot, field descriptions, field scope, abstention, vocabulary,
 * identity resolution, or nothing at all.
 */
export type CorrectionErrorClass =
  | "MISSED_IN_SOURCE"
  | "MISREAD_SOURCE"
  | "WRONG_FIELD"
  | "OVER_EXTRACTED"
  | "VOCABULARY"
  | "ABSENT_FROM_SOURCE"
  | "MISATTRIBUTED"
  | "CLINICAL_JUDGEMENT";

/**
 * How the class was arrived at. A class the doctor never looked at is weaker evidence
 * than one they arbitrated, and the training pass weights them apart — so the interface
 * has to report which of the two happened rather than flattening both to "classified".
 */
export type JustificationSource =
  | "auto"
  | "confirmed"
  | "overridden"
  | "manual";

/**
 * Classes where prose is REQUIRED, because neither the target field nor the corrected
 * value can carry the reason.
 *
 * The rule, applied per class rather than by fiat: a missed reading needs *which
 * document*; a misread passage needs *what it actually says*; an over-extraction needs
 * *why this real sentence must not fill this field*; a misattribution needs *which
 * patient, episode or date*; a clinical judgement needs the knowledge itself.
 *
 * The three left out are fully described by their structure: a target field names both
 * sides of a misfiling, an old→new pair IS a vocabulary rule, and "this appears nowhere"
 * adds nothing to the value being removed. They get an OPTIONAL box instead
 * (`CLASSES_WITH_OPTIONAL_PROSE`).
 *
 * `MISSED_IN_SOURCE` is here because the passage picker that used to carry its locus is
 * gone — it produced zero citations across 169 classified corrections in production, and
 * a class whose repair is document selection is unactionable without a document.
 *
 * Mirror of `correction_classes.CLASSES_REQUIRING_PROSE`; the server refuses a row that
 * breaks it, and this list is what keeps the doctor from discovering that by being
 * rejected.
 */
export const CLASSES_REQUIRING_PROSE: readonly CorrectionErrorClass[] = [
  "MISSED_IN_SOURCE",
  "MISREAD_SOURCE",
  "OVER_EXTRACTED",
  "MISATTRIBUTED",
  "CLINICAL_JUDGEMENT",
];

/**
 * Classes where prose is OPTIONAL: the box is shown and never blocks a save. Structure
 * describes these three, but not always fully — « Autre » picked from a closed list says
 * nothing about the term that was meant.
 *
 * Mirror of `correction_classes.CLASSES_WITH_OPTIONAL_PROSE`.
 */
export const CLASSES_WITH_OPTIONAL_PROSE: readonly CorrectionErrorClass[] = [
  "WRONG_FIELD",
  "VOCABULARY",
  "ABSENT_FROM_SOURCE",
];

/**
 * Classes qu'un clinicien peut affirmer SANS donner la bonne valeur.
 *
 * Le geste « c'est faux et je ne sais pas quoi mettre » a besoin d'une raison comme tout
 * désaccord, mais quatre des huit classes présupposent la valeur corrigée et deviennent
 * illisibles sans elle : « oublié » et « mal lu » disent que le dossier contient la
 * réponse — donnez-la ; « vocabulaire » dit que la valeur corrigée EST la réponse ; et
 * « jugement clinique » décrit d'où cette valeur vient. Les quatre retenues disent que
 * l'extraction est fausse en elle-même : elle a rempli un champ qui devait rester vide,
 * elle a inventé, elle a pris le fait chez un autre patient, ou elle a rangé une valeur
 * juste dans le mauvais champ.
 *
 * Miroir de ``correction_classes.CLASSES_ASSERTABLE_WITHOUT_A_VALUE`` — le serveur refuse
 * les autres, cette liste évite au médecin de le découvrir en étant rejeté.
 */
export const CLASSES_ASSERTABLE_WITHOUT_A_VALUE: readonly CorrectionErrorClass[] = [
  "OVER_EXTRACTED",
  "ABSENT_FROM_SOURCE",
  "MISATTRIBUTED",
  "WRONG_FIELD",
];

/** The one class that must name another field: a boundary needs two sides. */
export const CLASSES_REQUIRING_TARGET_FIELD: readonly CorrectionErrorClass[] = [
  "WRONG_FIELD",
];

/** An ontology field a misfiled value could have belonged to. */
export interface SiblingField {
  field_key: string;
  name: string;
  group_name: string | null;
  description: string | null;
}

/** What the machine believes about this correction, and why. */
export interface CorrectionSuggestion {
  /** A single class, when the evidence settles one. */
  error_class: CorrectionErrorClass | null;
  /**
   * The subset a POSITIVE find leaves open. Empty means no narrowing — which is now the
   * common case, and deliberately so: the backend used to eliminate five of the eight
   * whenever the corrected value was not found among the record's newest 40 passages,
   * which fired on 284 of 300 real corrections and dimmed the class doctors pick most
   * often. Narrowing on a failure to find was not evidence.
   */
  candidate_classes: CorrectionErrorClass[];
  rationale: string;
  /** Whether the corrected value was located in the registry's passages. Always false on
   * the flat surface, where the registry is not consulted at all. */
  new_value_found: boolean;
}

/** Facts worth showing about this correction. Never a reason to skip the question. */
export interface JustificationHints {
  offer_vocabulary: boolean;
  repeated: { count: number } | null;
  reason: string;
}

export interface CorrectionContext {
  field: string;
  field_type: string;
  old_value: string | number | boolean | string[] | null;
  suggestion: CorrectionSuggestion;
  hints: JustificationHints;
  sibling_fields: SiblingField[];
}

export interface CorrectionContextResponse {
  ok: boolean;
  error?: string;
  message?: string;
  data?: CorrectionContext;
}
